import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTheme } from "next-themes";
import { Sun, Moon, List, X } from "@phosphor-icons/react";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/government", label: "Government" },
  { to: "/projects", label: "Projects" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { resolvedTheme, setTheme } = useTheme();
  const { pathname } = useLocation();
  const onHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const solid = scrolled || open || !onHome;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        solid ? "glass-nav border-b border-line" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src="/brand/logo-icon.png" alt="FOSCHE logo" className="h-11 md:h-[52px] w-auto" />
          <span className="flex items-baseline gap-2">
            <span className={`font-logo text-xl md:text-2xl ${solid ? "text-ink" : "text-white"}`}>FOSCHE</span>
            <span className="font-logo text-[10px] md:text-[11px] text-[#6A7CB5]">LLC</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[13px] font-medium tracking-wide transition-colors ${
                  isActive
                    ? "text-blueprint"
                    : solid
                    ? "text-sub hover:text-ink"
                    : "text-white/80 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="theme-toggle-btn"
            onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
            aria-label="Toggle theme"
            className={`p-2 border transition-colors ${
              solid ? "border-line text-ink hover:border-[var(--blueprint)]" : "border-white/30 text-white hover:border-white"
            }`}
          >
            {resolvedTheme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <Link
            to="/quote"
            data-testid="nav-quote-btn"
            className="hidden sm:inline-flex items-center px-5 py-2.5 bg-blueprint text-white text-[13px] font-medium tracking-wide transition-all hover:bg-[var(--gold)] hover:text-black"
          >
            Request a Quote
          </Link>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            className={`lg:hidden p-2 border ${solid ? "border-line text-ink" : "border-white/30 text-white"}`}
          >
            {open ? <X size={18} /> : <List size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden glass-nav border-t border-line">
          <nav className="px-5 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `font-heading text-xl tracking-tight ${isActive ? "text-blueprint" : "text-ink"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/quote" className="mt-2 inline-flex w-fit px-5 py-2.5 bg-blueprint text-white text-sm font-medium">
              Request a Quote
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
